import type { SubscriptionPlan, UserSubscription, QuotaInfo } from '../types';
import { useTranslation } from 'react-i18next';

// Updated subscription plans
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    stripe_id: 'free',
    name: 'utils.subscription_plans.free',
    price: 0,
    analyses: 5,
    features: [
      'utils.subscription_plans.features.free_analyses',
      'utils.subscription_plans.features.complete_analysis',
      'utils.subscription_plans.features.compatibility_score',
      'utils.subscription_plans.features.basic_recommendations',
      'utils.subscription_plans.features.cover_letter_generation'
    ]
  },
  {
    id: 'decouverte',
    stripe_id:'price_1S8Gh9HvfcwGRwB5ux3T9Qn4',
    name: 'utils.subscription_plans.discovery_pack',
    price: 3.99,
    analyses: 10,
    features: [
      'utils.subscription_plans.features.additional_analyses_10',
      'utils.subscription_plans.features.complete_analysis',
      'utils.subscription_plans.features.detailed_score',
      'utils.subscription_plans.features.advanced_recommendations',
      'utils.subscription_plans.features.cover_letter_generation',
  ]
  },
  {
    id: 'avance',
    stripe_id : 'price_1S8GgcHvfcwGRwB5GZaJRqFB',
    name: 'utils.subscription_plans.advanced_pack',
    price: 6.99,
    analyses: 30,
    features: [
      'utils.subscription_plans.features.additional_analyses_30',
      'utils.subscription_plans.features.complete_analysis',
      'utils.subscription_plans.features.detailed_score',
      'utils.subscription_plans.features.advanced_recommendations',
      'utils.subscription_plans.features.cover_letter_generation',
      'utils.subscription_plans.features.multiple_cv_comparison',
      'utils.subscription_plans.features.priority_support'
    ],
    popular: true
  },
  {
    id: 'pro',
    stripe_id : 'price_1S8GfzHvfcwGRwB5KCSyIB0T',
    name: 'utils.subscription_plans.pro_pack',
    price: 14.99,
    analyses: 100,
    features: [
      'utils.subscription_plans.features.additional_analyses_100',
      'utils.subscription_plans.features.complete_analysis',
      'utils.subscription_plans.features.detailed_score',
      'utils.subscription_plans.features.advanced_recommendations',
      'utils.subscription_plans.features.cover_letter_generation',
      'utils.subscription_plans.features.multiple_cv_comparison',
      'utils.subscription_plans.features.analysis_history',
      'utils.subscription_plans.features.priority_support',
      'utils.subscription_plans.features.early_access'
    ]
  }
];

// Special welcome offer plan
export const WELCOME_OFFER_PLAN: SubscriptionPlan = {
  id: 'welcome-offer',
  stripe_id:'price_1S4yRK2YothPzFuBRqh94vBd',
  name: 'utils.subscription_plans.welcome_offer',
  price: 1.99,
  analyses: 10,
  features: [
    'utils.subscription_plans.features.additional_analyses_10',
    'utils.subscription_plans.features.complete_analysis',
    'utils.subscription_plans.features.detailed_score',
    'utils.subscription_plans.features.advanced_recommendations',
    'utils.subscription_plans.features.cover_letter_generation',
    'utils.subscription_plans.features.exclusive_offer'
  ]
};

// localStorage management for simulation
const STORAGE_KEY = 'cv_analyzer_subscription';

// Helper function to translate subscription plan data
export const getTranslatedSubscriptionPlans = (t: (key: string) => string): SubscriptionPlan[] => {
  return [
    {
      id: 'free',
      stripe_id: 'free',
      name: t('utils.subscription_plans.free'),
      price: 0,
      analyses: 5,
      features: [
        t('utils.subscription_plans.features.free_analyses'),
        t('utils.subscription_plans.features.complete_analysis'),
        t('utils.subscription_plans.features.compatibility_score'),
        t('utils.subscription_plans.features.basic_recommendations'),
        t('utils.subscription_plans.features.cover_letter_generation')
      ]
    },
    {
      id: 'decouverte',
      stripe_id : 'price_1S8Gh9HvfcwGRwB5ux3T9Qn4',
      name: t('utils.subscription_plans.discovery_pack'),
      price: 3.99,
      analyses: 10,
      features: [
        t('utils.subscription_plans.features.additional_analyses_10'),
        t('utils.subscription_plans.features.complete_analysis'),
        t('utils.subscription_plans.features.detailed_score'),
        t('utils.subscription_plans.features.advanced_recommendations'),
        t('utils.subscription_plans.features.cover_letter_generation'),
      ]
    },
    {
      id: 'avance',
      stripe_id: 'price_1S8GgcHvfcwGRwB5GZaJRqFB',
      name: t('utils.subscription_plans.advanced_pack'),
      price: 6.99,
      analyses: 30,
      features: [
        t('utils.subscription_plans.features.additional_analyses_30'),
        t('utils.subscription_plans.features.complete_analysis'),
        t('utils.subscription_plans.features.detailed_score'),
        t('utils.subscription_plans.features.advanced_recommendations'),
        t('utils.subscription_plans.features.cover_letter_generation'),
        t('utils.subscription_plans.features.multiple_cv_comparison'),
        t('utils.subscription_plans.features.priority_support')
      ],
      popular: true
    },
    {
      id: 'pro',
      stripe_id : 'price_1S8GfzHvfcwGRwB5KCSyIB0T',
      name: t('utils.subscription_plans.pro_pack'),
      price: 14.99,
      analyses: 100,
      features: [
        t('utils.subscription_plans.features.additional_analyses_100'),
        t('utils.subscription_plans.features.complete_analysis'),
        t('utils.subscription_plans.features.detailed_score'),
        t('utils.subscription_plans.features.advanced_recommendations'),
        t('utils.subscription_plans.features.cover_letter_generation'),
        t('utils.subscription_plans.features.multiple_cv_comparison'),
        t('utils.subscription_plans.features.analysis_history'),
        t('utils.subscription_plans.features.priority_support'),
        t('utils.subscription_plans.features.early_access')
      ]
    }
  ];
};

export const getTranslatedWelcomeOfferPlan = (t: (key: string) => string): SubscriptionPlan => {
  return {
    id: 'welcome-offer',
    stripe_id : 'price_1S4yRK2YothPzFuBRqh94vBd',
    name: t('utils.subscription_plans.welcome_offer'),
    price: 1.99,
    analyses: 10,
    features: [
      t('utils.subscription_plans.features.additional_analyses_10'),
      t('utils.subscription_plans.features.complete_analysis'),
      t('utils.subscription_plans.features.detailed_score'),
      t('utils.subscription_plans.features.advanced_recommendations'),
      t('utils.subscription_plans.features.cover_letter_generation'),
      t('utils.subscription_plans.features.pdf_export'),
      t('utils.subscription_plans.features.exclusive_offer')
    ]
  };
};



export const getStoredSubscription = (): UserSubscription => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const subscription = JSON.parse(stored);
      
      // CORRECTION: Ensure analysesRemaining is consistent
      if (subscription.analysesRemaining === undefined || subscription.analysesRemaining < 0) {
        const plan = getCurrentPlan(subscription.plan);
        const limit = plan?.analyses || 5;
        subscription.analysesRemaining = Math.max(0, limit - (subscription.analysesUsed || 0));
      }
      
      return subscription;
    }
  } catch (error) {
    console.error('Error reading subscription:', error);
  }
  
  // Default values for new users - 5 free analyses per month
  return {
    plan: 'free',
    analysesUsed: 0,
    analysesRemaining: 5,
    isActive: true
  };
};

export const saveSubscription = (subscription: UserSubscription): void => {
  try {
    // localStorage.setItem(STORAGE_KEY, JSON.stringify(subscription));
  } catch (error) {
    console.error('Error saving subscription:', error);
  }
};

export const getCurrentPlan = (planId: string): SubscriptionPlan | null => {
  // console.log("planId",planId)
  // Use the translated plans function with a default translation function
  // const defaultT = (key: string) => key; // Fallback to key if no translation function provided
  // const plans = getTranslatedSubscriptionPlans(defaultT);
  // return 
 return SUBSCRIPTION_PLANS.find(plan => plan.id === planId) || null;
};

export const getQuotaInfo = (subscription: UserSubscription): QuotaInfo => {
  const plan = getCurrentPlan(subscription.plan);
  const limit = plan?.analyses || 5;
  const current = subscription.analysesUsed || 0;
  
  // CORRECTION: Use analysesRemaining directly if it exists
  let remaining = subscription.analysesRemaining;
  if (remaining === undefined || remaining < 0) {
    remaining = Math.max(0, limit - current);
  }
  
  return {
    current,
    limit: Math.max(limit, current + remaining), // Ensure limit is consistent
    percentage: Math.round((current / Math.max(limit, current + remaining)) * 100),
    canAnalyze: remaining > 0 && subscription.isActive
  };
};

export const consumeAnalysis = (): boolean => {
  const subscription = getStoredSubscription();
  const quota = getQuotaInfo(subscription);
  
  if (!quota.canAnalyze) {
    return false;
  }
  
  subscription.analysesUsed = (subscription.analysesUsed || 0) + 1;
  subscription.analysesRemaining = Math.max(0, (subscription.analysesRemaining || 0) - 1);
  
  saveSubscription(subscription);
  return true;
};

export const upgradePlan = (newPlanId: string): boolean => {
  const newPlan = getCurrentPlan(newPlanId);
  if (!newPlan) return false;
  
  const subscription = getStoredSubscription();
  
  // Record purchase in history
  recordPurchase(newPlanId, newPlan.price);
  
  // For packs, add analyses to existing quota
  if (newPlanId !== 'free') {
    subscription.analysesRemaining = (subscription.analysesRemaining || 0) + newPlan.analyses;
    // Keep free plan but add analyses
    subscription.plan = 'free';
  } else {
    subscription.plan = newPlanId;
    subscription.analysesUsed = 0;
    subscription.analysesRemaining = newPlan.analyses;
  }
  
  subscription.isActive = true;
  
  // For packs, no renewal date (one-time purchase)
  if (newPlanId !== 'free') {
    delete subscription.renewalDate;
  }
  
  saveSubscription(subscription);
  return true;
};

// Function to process welcome offer
export const upgradeWithWelcomeOffer = (): boolean => {
  const subscription = getStoredSubscription();
  
  // Record welcome offer purchase
  recordPurchase('welcome-offer', WELCOME_OFFER_PLAN.price);
  
  // Add offer analyses
  subscription.analysesRemaining = (subscription.analysesRemaining || 0) + WELCOME_OFFER_PLAN.analyses;
  subscription.plan = 'free';
  subscription.isActive = true;
  
  saveSubscription(subscription);
  return true;
};

// Function to record a purchase
export const recordPurchase = (planId: string, price: number): void => {
  try {
    const purchaseHistory = JSON.parse(localStorage.getItem('cv_analyzer_purchase_history') || '[]');
    purchaseHistory.push({
      planId,
      price,
      date: new Date().toISOString(),
      timestamp: Date.now()
    });
    // localStorage.setItem('cv_analyzer_purchase_history', JSON.stringify(purchaseHistory));
  } catch (error) {
    console.error('Error recording purchase:', error);
  }
};

// Check if user has ever made a purchase
export const hasUserEverPurchased = (): boolean => {
  try {
    const purchaseHistory = localStorage.getItem('cv_analyzer_purchase_history');
    return purchaseHistory && JSON.parse(purchaseHistory).length > 0;
  } catch {
    return false;
  }
};

// NEW FUNCTION: Add bonus analyses
export const addBonusAnalyses = (bonusCount: number): boolean => {
  try {
    const subscription = getStoredSubscription();
    subscription.analysesRemaining = (subscription.analysesRemaining || 0) + bonusCount;
    saveSubscription(subscription);
    return true;
  } catch (error) {
    console.error('Error adding bonus analyses:', error);
    return false;
  }
};

export const resetQuota = (): void => {
  const subscription = getStoredSubscription();
  
  // Monthly reset: restore 5 free analyses
  subscription.analysesUsed = 0;
  subscription.analysesRemaining = 5;
  subscription.plan = 'free';
  
  saveSubscription(subscription);
};

// NEW FUNCTION: Reset quota for testing
export const resetQuotaForTesting = (): void => {
  const subscription = getStoredSubscription();
  
  // Give 50 analyses for testing
  subscription.analysesUsed = 0;
  subscription.analysesRemaining = 50;
  subscription.plan = 'free';
  subscription.isActive = true;
  
  saveSubscription(subscription);
};

export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export const getQuotaColor = (percentage: number): string => {
  if (percentage >= 90) return '#f44336'; // Red
  if (percentage >= 70) return '#ff9800'; // Orange
  if (percentage >= 50) return '#ffc107'; // Yellow
  return '#4caf50'; // Green
};